import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ShieldCheck, Loader2 } from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { Negociacao } from '../../../../types';
import { validarCPF } from '../../../../src/lib/utils';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../../src/lib/logger';
import { consultarLemit } from '../../../../src/services/lemit';

interface AdicionarAvalistaModalProps {
    isOpen: boolean;
    onClose: () => void;
    negotiation: Negociacao;
    onSaved: () => void;
}

const maskCPF = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
};

const maskPhone = (value: string) => {
    return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d)(\d{4})$/, '$1-$2')
        .slice(0, 15);
};

/** Traduz os erros que o cadastro de avalista realmente produz. Os dois vêm do
 *  banco com texto de Postgres cru, que não diz ao usuário o que fazer:
 *  o índice único parcial (avalista ativo já existe) e o trigger
 *  avalista_exige_reprovacao (negociação não está reprovada no crédito). */
const mensagemErroCadastro = (error: any): string => {
    const msg: string = error?.message || '';
    if (error?.code === '23505' || /duplicate key|idx_negociacao_avalistas_ativo/i.test(msg)) {
        return 'esta negociação já tem um avalista ativo. Remova o atual antes de cadastrar outro.';
    }
    if (/análise cadastral reprovada|analise cadastral reprovada/i.test(msg)) {
        return 'o avalista só pode ser cadastrado quando a validação de dados está com decisão "Reprovado".';
    }
    return msg || 'Erro desconhecido';
};

const AdicionarAvalistaModal: React.FC<AdicionarAvalistaModalProps> = ({ isOpen, onClose, negotiation, onSaved }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ nome_completo: '', cpf: '', email: '', telefone: '', endereco: '' });

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.nome_completo.trim()) {
            addToast('O nome completo do avalista é obrigatório.', 'warning');
            return;
        }
        if (!validarCPF(formData.cpf)) {
            addToast('CPF do avalista inválido.', 'warning');
            return;
        }
        if (!formData.email.trim()) {
            addToast('O e-mail do avalista é obrigatório — é usado para o envio da assinatura.', 'warning');
            return;
        }
        if (!formData.endereco.trim()) {
            addToast('O endereço do avalista é obrigatório — ele sai no Termo de Anuência.', 'warning');
            return;
        }

        const cpfDigits = formData.cpf.replace(/\D/g, '');
        setLoading(true);

        try {
            // Só existe 1 avalista ativo por negociação (índice único parcial
            // idx_negociacao_avalistas_ativo). Sem esta checagem, uma tela com a
            // lista de avalistas desatualizada deixa o usuário preencher o modal
            // inteiro para receber "duplicate key value violates unique
            // constraint" — que ele lê como "o cadastro não finaliza".
            const { data: ativo, error: ativoError } = await supabase
                .from('negociacao_avalistas')
                .select('id, nome_completo, status')
                .eq('negociacao_id', negotiation.id)
                .neq('status', 'removido')
                .maybeSingle();
            if (ativoError) throw ativoError;
            if (ativo) {
                addToast(
                    `Esta negociação já tem um avalista ativo (${ativo.nome_completo}). Remova o atual antes de cadastrar outro.`,
                    'warning',
                );
                onSaved();
                onClose();
                return;
            }

            const { data: inserted, error } = await supabase
                .from('negociacao_avalistas')
                .insert({
                    negociacao_id: negotiation.id,
                    nome_completo: formData.nome_completo.trim(),
                    cpf: cpfDigits,
                    email: formData.email.trim() || null,
                    telefone: formData.telefone ? formData.telefone.replace(/\D/g, '') : null,
                    endereco: formData.endereco.trim(),
                })
                .select('id')
                .single();

            if (error) throw error;

            await logNegotiationAction(negotiation.id, user?.id, 'cadastrou avalista', {
                nome: formData.nome_completo.trim(),
            });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'cadastrar_avalista', origem: 'acao' });

            addToast('Avalista cadastrado com sucesso!', 'success');
            setFormData({ nome_completo: '', cpf: '', email: '', telefone: '', endereco: '' });

            // Consulta de crédito do avalista — melhor esforço, não bloqueia o cadastro.
            // Disparada antes de fechar porque o fechamento desmonta este componente.
            consultarCreditoAvalista(inserted.id, cpfDigits, negotiation.id);

            onSaved();
            onClose();
        } catch (error: any) {
            console.error('Erro ao cadastrar avalista:', error);
            addToast('Erro ao cadastrar avalista: ' + mensagemErroCadastro(error), 'error');
        } finally {
            setLoading(false);
        }
    };

    /** Enriquecimento do CPF do avalista, gravado em consulta_credito_json para o
     *  analista usar na reanálise. Melhor esforço: falha de qualquer fonte não
     *  desfaz o cadastro.
     *
     *  A consulta Serasa foi REMOVIDA daqui (28/08/2026, decisão do produto): a API
     *  não é mais usada no CRM. Ela já vinha falhando em 100% das chamadas desde
     *  04/08/2026 (a Serasa passou a negar a transação BPCB para a credencial da
     *  Vigor), e o resultado ficava só num console.warn — o avalista era cadastrado
     *  "com sucesso" e seguia para reanálise sem esse dado, sem aviso nenhum. */
    const consultarCreditoAvalista = async (avalistaId: number, cpf: string, negociacaoId: number) => {
        const consulta: Record<string, any> = {};

        try {
            const res = await supabase.functions.invoke('consultar-cpf-cnpj', { body: { document: cpf, type: 'cpf' } });
            if (!res.error && !res.data?.error) consulta.cpf_cnpj = res.data;
        } catch (err) {
            console.warn('Consulta CPF/CNPJ do avalista falhou:', err);
        }

        try {
            const lemitResult = await consultarLemit({ criterio: 'cpf', valor: cpf, negociacaoId });
            consulta.lemit = lemitResult.data;
        } catch (err) {
            console.warn('Consulta Lemit do avalista falhou:', err);
        }

        if (Object.keys(consulta).length > 0) {
            const { error } = await supabase
                .from('negociacao_avalistas')
                .update({ consulta_credito_json: consulta })
                .eq('id', avalistaId);
            if (error) console.warn('Falha ao gravar consulta_credito_json do avalista:', error.message);
        }
    };

    // Portal: aberto de dentro de "Termos Reprovados", o fixed ficava preso ao modal pai
    // (que tem transform) e saía recortado, escondendo os campos de baixo.
    return createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100000] p-4 animate-fade-in" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-slide-up border border-gray-100 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                            <ShieldCheck size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Adicionar avalista</h2>
                            <p className="text-xs text-gray-500 dark:text-slate-300">Caminho de recuperação para negociação reprovada</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-full transition-colors text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 shadow-sm border border-transparent hover:border-gray-200 dark:hover:border-slate-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">Nome completo *</label>
                        <input
                            type="text"
                            value={formData.nome_completo}
                            onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder="Nome completo do avalista"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">CPF *</label>
                        <input
                            type="text"
                            value={formData.cpf}
                            onChange={(e) => setFormData({ ...formData, cpf: maskCPF(e.target.value) })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder="000.000.000-00"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">E-mail *</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder="email@exemplo.com"
                            required
                        />
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Usado para enviar o link de assinatura via Autentique.</p>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">Telefone</label>
                        <input
                            type="text"
                            value={formData.telefone}
                            onChange={(e) => setFormData({ ...formData, telefone: maskPhone(e.target.value) })}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow"
                            placeholder="(00) 00000-0000"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-white uppercase mb-1">Endereço completo *</label>
                        <textarea
                            value={formData.endereco}
                            onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                            rows={2}
                            className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white transition-shadow resize-none"
                            placeholder="Rua, número, bairro, cidade/UF, CEP"
                            required
                        />
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Sai no Termo de Anuência junto com o CPF e o e-mail.</p>
                    </div>

                    <p className="text-[11px] text-gray-400 dark:text-slate-500">
                        Ao salvar, o CPF do avalista é automaticamente consultado na Lemit para embasar a reanálise.
                    </p>

                    <div className="pt-4 flex justify-end gap-3 border-t border-gray-100 dark:border-slate-800 mt-6 pb-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-white bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-colors"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary/90 rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                        >
                            {loading && <Loader2 size={16} className="animate-spin" />}
                            Cadastrar avalista
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    );
};

export default AdicionarAvalistaModal;
